import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Avatar, Tooltip, IconButton,
  InputAdornment, LinearProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, RadioGroup, FormControlLabel, Radio, FormLabel,
  TablePagination,
} from '@mui/material';
import { Refresh, ArrowBack, Search, AttachMoney, Receipt, CheckCircle, Delete, Add } from '@mui/icons-material';
import { InvokeApi, postImg, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';

const TX_TABS = [
  { key: 'Success', label: 'Paid', bg: '#e8f5e9', color: '#2e7d32' },
  { key: 'Pending', label: 'Pending', bg: '#fff3e0', color: '#e65100' },
  { key: 'Failed', label: 'Failed', bg: '#ffebee', color: '#c62828' },
];

const fmtINR = (n) => {
  const num = Number(n || 0);
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toLocaleString('en-IN')}`;
};

export default function Fees() {
  const { user } = useAuth();
  const isAccountant = user?.role === 'Accountant';
  const [view, setView] = useState('grid');
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [searchStudent, setSearchStudent] = useState('');
  const [feesBalance, setFeesBalance] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [txTab, setTxTab] = useState('Success');
  const [detailLoading, setDetailLoading] = useState(false);
  const [txLoading, setTxLoading] = useState(false);
  const [globalTx, setGlobalTx] = useState([]);
  const [globalTxTab, setGlobalTxTab] = useState('Success');
  const [globalLoading, setGlobalLoading] = useState(false);
  const [acYear, setAcYear] = useState('');
  const [academicYears, setAcademicYears] = useState([]);
  const [receiptDialog, setReceiptDialog] = useState(null);
  const [collectDialog, setCollectDialog] = useState(false);
  const [collectForm, setCollectForm] = useState({
    recipt_no: '', recipt_name: '',
    registrationFee: '', admissionFee: '', transportFee: '', miscellaneousFee: '',
    pay_method: 'Cash',
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPath, setReceiptPath] = useState('');
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [collectSaving, setCollectSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const init = async () => {
      const data = await InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {});
      const years = data?.academic_years || [];
      setAcademicYears(years);
      const current = years.find(y => y.is_current) || years[0];
      if (current) setAcYear(current.value || '');
    };
    init();
  }, []);

  const loadGlobalTx = useCallback(async () => {
    if (!acYear) return;
    setGlobalLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getFeesDate, {
      status: globalTxTab,
      academic_year: acYear,
    });
    setGlobalTx(data?.fees || []);
    setGlobalLoading(false);
  }, [acYear, globalTxTab]);

  useEffect(() => { if (view === 'grid') loadGlobalTx(); }, [loadGlobalTx, view]);

  const handleClassSelect = useCallback(async (cls) => {
    setSelectedClass(cls);
    setView('students');
    setSearchStudent('');
    setStudentsLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getStudentsByStdSecAcYear, {
      standard: cls.standard, section: cls.section, acYear: cls.acYear,
    });
    setStudents(data?.students || []);
    setStudentsLoading(false);
  }, []);

  const handleStudentSelect = useCallback(async (student) => {
    setSelectedStudent(student);
    setView('detail');
    setTxTab('Success');
    setDetailLoading(true);
    const balData = await InvokeApi(Config.main_url + apiList.getFeesBalance, {
      stdId: student.ID,
      academic_year: selectedClass?.acYear || acYear,
    });
    setFeesBalance(balData?.fees || {});
    setDetailLoading(false);
    loadStudentTx(student.ID, 'Success');
  }, [selectedClass, acYear]);

  const loadStudentTx = useCallback(async (studentId, status) => {
    setTxLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getFees, {
      stdId: studentId,
      status,
      academic_year: selectedClass?.acYear || acYear,
    });
    setTransactions(data?.fees || []);
    setTxLoading(false);
  }, [selectedClass, acYear]);

  const switchTxTab = (tab) => {
    setTxTab(tab);
    if (selectedStudent) loadStudentTx(selectedStudent.ID, tab);
  };

  const filteredStudents = students.filter(s =>
    !searchStudent || s.full_name?.toLowerCase().includes(searchStudent.toLowerCase())
  );

  const txTotal = transactions.reduce((sum, r) =>
    sum + Number(r.registration_fee || 0) + Number(r.admission_fee || 0) +
    Number(r.transport_fee || 0) + Number(r.miscellaneous_fee || 0), 0);

  const globalTxTotal = globalTx.reduce((sum, r) =>
    sum + Number(r.registration_fee || 0) + Number(r.admission_fee || 0) +
    Number(r.transport_fee || 0) + Number(r.miscellaneous_fee || 0), 0);

  const handleReceiptUpload = async (file) => {
    setReceiptUploading(true);
    const fd = new FormData();
    fd.append('fees_recipt', file);
    const res = await postImg(Config.main_url + apiList.addFeesRecipt, fd);
    if (res?.code === '200') setReceiptPath(res.imageName || '');
    setReceiptUploading(false);
  };

  const handleCollectFees = async () => {
    if (!collectForm.recipt_no || !collectForm.registrationFee) {
      alert('Receipt number and Tuition Fee are required'); return;
    }
    setCollectSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addFees, {
      stdId: selectedStudent.ID,
      recipt_no: collectForm.recipt_no,
      recipt_name: collectForm.recipt_name,
      registrationFee: collectForm.registrationFee || '0',
      admissionFee: collectForm.admissionFee || '0',
      transportFee: collectForm.transportFee || '0',
      miscellaneousFee: collectForm.miscellaneousFee || '0',
      feesRecipt: receiptPath || '',
      pay_method: collectForm.pay_method || 'Cash',
      status:'Pending',
      createdBy: '1'
    });
    setCollectSaving(false);
    if (data?.code === '200') {
      setCollectDialog(false);
      setCollectForm({ recipt_no: '', recipt_name: '', registrationFee: '', admissionFee: '', transportFee: '', miscellaneousFee: '', pay_method: 'Cash' });
      setReceiptFile(null);
      setReceiptPath('');
      setTxTab('Pending');
      const balData = await InvokeApi(Config.main_url + apiList.getFeesBalance, {
        stdId: selectedStudent.ID,
        academic_year: selectedClass?.acYear || acYear,
      });
      setFeesBalance(balData?.fees || {});
      loadStudentTx(selectedStudent.ID, 'Pending');
    } else alert(data?.message || 'Failed to add fees');
  };

  const handleTxAction = async (txId, newStatus) => {
    setActionLoading(p => ({ ...p, [txId]: true }));
    const data = await InvokeApi(Config.main_url + apiList.deleteFees, {
      id: txId,
      status: newStatus,
    });
    setActionLoading(p => ({ ...p, [txId]: false }));
    if (data?.code === '200') {
      const balData = await InvokeApi(Config.main_url + apiList.getFeesBalance, {
        stdId: selectedStudent.ID,
        academic_year: selectedClass?.acYear || acYear,
      });
      setFeesBalance(balData?.fees || {});
      loadStudentTx(selectedStudent.ID, txTab);
    } else alert(data?.message || 'Failed');
  };

  const cf = (k) => (e) => setCollectForm(p => ({ ...p, [k]: e.target.value }));
  const paginated = globalTx.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">Fees Manager</Typography>
            <Typography variant="body2" color="text.secondary">Select a class to view student fees</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField select size="small" label="Academic Year" value={acYear}
              onChange={e => setAcYear(e.target.value)} sx={{ minWidth: 160 }}>
              {academicYears.map(y => <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>)}
            </TextField>
            <Tooltip title="Refresh">
              <IconButton onClick={loadGlobalTx} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3, mb: 3 }}>
          <ClassSectionGrid onSelect={handleClassSelect} acYear={acYear} onAcYearChange={setAcYear} />
        </Card>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography fontWeight={700} color="#1a237e" fontSize={16}>All Transactions</Typography>
                <Typography fontSize={12} color="text.secondary">
                  {acYear} · {globalTx.length} records · Total: {fmtINR(globalTxTotal)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {TX_TABS.map(t => (
                  <Chip key={t.key} label={t.label} clickable onClick={() => setGlobalTxTab(t.key)}
                    sx={{
                      fontWeight: 700, fontSize: 12,
                      background: globalTxTab === t.key ? t.bg : '#f5f6fa',
                      color: globalTxTab === t.key ? t.color : 'text.secondary',
                      border: globalTxTab === t.key ? `1.5px solid ${t.color}` : '1.5px solid transparent',
                    }}
                  />
                ))}
              </Box>
            </Box>

            {globalLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={28} /></Box>
            ) : paginated.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Receipt sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                <Typography color="text.secondary">No {globalTxTab.toLowerCase()} transactions for {acYear}</Typography>
              </Box>
            ) : (
              <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#f5f6fa' }}>
                      {['#', 'Receipt No', 'Name', 'Amount', 'Status', 'Date', 'Receipt'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.map((r, i) => {
                      const total = Number(r.registration_fee || 0) + Number(r.admission_fee || 0) +
                        Number(r.transport_fee || 0) + Number(r.miscellaneous_fee || 0);
                      const tab = TX_TABS.find(t => t.key === r.status) || TX_TABS[0];
                      return (
                        <TableRow key={r.ID || i} hover>
                          <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{i + 1}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.recipt_no || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.recipt_name || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 13, fontWeight: 700, color: tab.color }}>{fmtINR(total)}</TableCell>
                          <TableCell><Chip label={r.status} size="small" sx={{ background: tab.bg, color: tab.color, fontWeight: 700, fontSize: 10 }} /></TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{r.created_at ? dayjs(r.created_at).format('DD MMM YYYY') : '—'}</TableCell>
                          <TableCell>
                            {r.fees_recipt_url ? (
                              <Button href={r.fees_recipt_url} size="small" target="_blank" variant="outlined"
                                sx={{ borderRadius: 2, fontSize: 10, color: '#1a237e', borderColor: '#1a237e', minWidth: 0, px: 1 }}>
                                View
                              </Button>
                            ) : (
                              <Typography sx={{ fontSize: 10, fontWeight: 400 }}>No Receipt</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={globalTx.length} page={page}
                onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }} />
            </>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (view === 'students') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">Fees Manager</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedClass?.standard} - {selectedClass?.section} · {selectedClass?.acYear} · {students.length} students
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView('grid')}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Back</Button>
        </Box>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
            <TextField size="small" placeholder="Search student..." value={searchStudent}
              onChange={e => setSearchStudent(e.target.value)} sx={{ width: 300 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          </Box>

          {studentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['#', 'Student', 'Roll No', 'View Fees'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudents.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>No students found</TableCell></TableRow>
                  ) : filteredStudents.map((s, i) => (
                    <TableRow key={s.ID} hover sx={{ cursor: 'pointer' }} onClick={() => handleStudentSelect(s)}>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={s.profile_pic} sx={{ width: 36, height: 36, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 14, fontWeight: 700, color: '#fff' }}>
                            {s.full_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600} fontSize={13}>{s.full_name}</Typography>
                            <Typography fontSize={11} color="text.secondary">ID: #{s.ID}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{s.roll_number || '—'}</TableCell>
                      <TableCell>
                        <Button size="small" variant="contained" startIcon={<AttachMoney />}
                          onClick={e => { e.stopPropagation(); handleStudentSelect(s); }}
                          sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, fontSize: 11 }}>
                          View Fees
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Box>
    );
  }

  const balance = feesBalance;
  const total = parseFloat(balance.totalFees || 0);
  const paid = parseFloat(balance.totalFeesPaid || 0);
  const due = parseFloat(balance.totalFeesBalance || 0);
  const pct = total > 0 ? Math.round((paid / total) * 100) : 0;
  const isPaid = due <= 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Tooltip title="Back">
          <IconButton onClick={() => setView('students')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack sx={{ color: '#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">{selectedStudent?.full_name}</Typography>
          <Typography variant="body2" color="text.secondary">{selectedClass?.standard} - {selectedClass?.section} · Roll: {selectedStudent?.roll_number || '—'}</Typography>
        </Box>
      </Box>
        <Button variant="contained" startIcon={<Add />}
          onClick={() => {
            setCollectForm({ recipt_no: '', recipt_name: '', registrationFee: '', admissionFee: '', transportFee: '', miscellaneousFee: '', pay_method: 'Cash' });
            setReceiptFile(null); setReceiptPath('');
            setCollectDialog(true);
          }}
          sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 2, fontWeight: 700 }}>
          Collect Fees
        </Button>
      </Box>

      {detailLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
      ) : (
        <>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{
                p: 2.5, borderRadius: 3, mb: 3, textAlign: 'center',
                background: isPaid ? 'linear-gradient(135deg,#2e7d32,#388e3c)' : 'linear-gradient(135deg,#c62828,#d32f2f)',
              }}>
                <Typography fontSize={13} sx={{ color: 'rgba(255,255,255,0.8)' }}>Fee Status</Typography>
                <Typography fontSize={24} fontWeight={900} color="white">
                  {isPaid ? '✓ Fully Paid' : `${fmtINR(due)} Pending`}
                </Typography>
                <Typography fontSize={12} sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                  {selectedClass?.acYear}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography fontSize={12} color="text.secondary">Payment Progress</Typography>
                  <Typography fontSize={12} fontWeight={700} color="#1a237e">{pct}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct} sx={{
                  height: 8, borderRadius: 4, background: '#ffebee',
                  '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#2e7d32,#4caf50)', borderRadius: 4 },
                }} />
              </Box>

              <Grid container spacing={1.5}>
                {[
                  { label: 'Total Fees', value: fmtINR(total), bg: '#e8eaf6', color: '#1a237e' },
                  { label: 'Paid', value: fmtINR(paid), bg: '#e8f5e9', color: '#2e7d32' },
                  { label: 'Balance', value: fmtINR(due), bg: due > 0 ? '#ffebee' : '#e8f5e9', color: due > 0 ? '#c62828' : '#2e7d32' },
                ].map(s => (
                  <Grid item xs={4} key={s.label}>
                    <Box sx={{ p: 1.5, borderRadius: 2, background: s.bg, textAlign: 'center' }}>
                      <Typography fontSize={11} color="text.secondary">{s.label}</Typography>
                      <Typography fontSize={15} fontWeight={800} color={s.color}>{s.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography fontWeight={700} color="#1a237e" fontSize={15}>Transactions</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {TX_TABS.map(t => (
                    <Chip key={t.key} label={t.label} clickable onClick={() => switchTxTab(t.key)}
                      sx={{
                        fontWeight: 700, fontSize: 11,
                        background: txTab === t.key ? t.bg : '#f5f6fa',
                        color: txTab === t.key ? t.color : 'text.secondary',
                        border: txTab === t.key ? `1.5px solid ${t.color}` : '1.5px solid transparent',
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {txLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={28} /></Box>
              ) : transactions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Receipt sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                  <Typography color="text.secondary">No {txTab.toLowerCase()} transactions</Typography>
                </Box>
              ) : (
                transactions.map((tx, i) => {
                  const txAmt = Number(tx.registration_fee || 0) + Number(tx.admission_fee || 0) +
                    Number(tx.transport_fee || 0) + Number(tx.miscellaneous_fee || 0);
                  const tab = TX_TABS.find(t => t.key === tx.status) || TX_TABS[0];
                  return (
                    <Box key={tx.ID || i} sx={{
                      mb: 1.5, p: 2, borderRadius: 2,
                      border: '1px solid #f0f0f0',
                      borderLeft: `4px solid ${tab.color}`,
                      background: '#fafafa',
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                            <Typography fontSize={13} fontWeight={700} color="#1a237e">
                              Receipt: {tx.recipt_no || '—'}
                            </Typography>
                            <Chip label={tx.status} size="small" sx={{ background: tab.bg, color: tab.color, fontWeight: 700, fontSize: 10 }} />
                          </Box>
                          <Typography fontSize={12} color="text.secondary">{tx.recipt_name || '—'}</Typography>
                          <Typography fontSize={11} color="text.disabled">
                            {tx.created_at ? dayjs(tx.created_at).format('DD MMM YYYY') : '—'}
                          </Typography>
                          {tx.pay_method && (
                            <Chip
                              label={tx.pay_method}
                              size="small"
                              sx={{
                                mt: 0.5,
                                height: 18,
                                fontWeight: 700,
                                fontSize: 10,
                                background: tx.pay_method === 'Cash' ? '#e8f5e9' : '#e3f2fd',
                                color: tx.pay_method === 'Cash' ? '#2e7d32' : '#1565c0',
                              }}
                            />
                          )}
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            {[
                              { label: 'Tuition Fee', val: tx.registration_fee },
                              { label: 'Clinical Fee', val: tx.admission_fee },
                              { label: 'Mess Fee', val: tx.transport_fee },
                              { label: 'Hostel Fee', val: tx.miscellaneous_fee },
                            ].filter(f => parseFloat(f.val || 0) > 0).map(f => (
                              <Chip key={f.label} label={`${f.label}: ₹${parseFloat(f.val).toLocaleString()}`}
                                size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 10, height: 18 }} />
                            ))}
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography fontSize={20} fontWeight={900} color={tab.color}>{fmtINR(txAmt)}</Typography>
                          {tx.fees_recipt_url ? (
                            <Button size="small" variant="outlined" onClick={() => setReceiptDialog(tx.fees_recipt_url)}
                              sx={{ mt: 0.5, borderRadius: 2, fontSize: 10, color: '#1a237e', borderColor: '#1a237e' }}>
                              View Receipt
                            </Button>
                          ) : (
                            <Typography sx={{ fontSize: 10, fontWeight: 400 }}>No Receipt</Typography>
                          )}
                          {tx.status === 'Pending' && (
                            <Box sx={{ mt: 0.5 }}>
                              {actionLoading[tx.ID] ? (
                                <CircularProgress size={18} sx={{ color: '#2e7d32' }} />
                              ) : (
                                isAccountant ? "" : (
                                <Tooltip title="Approve — Mark as Paid">
                                  <Button size="small" variant="contained" startIcon={<CheckCircle />}
                                    onClick={() => handleTxAction(tx.ID, 'Success')}
                                    sx={{ background: '#2e7d32', borderRadius: 2, fontSize: 10, fontWeight: 700 }}>
                                    Approve
                                  </Button>
                                </Tooltip>)
                              )}
                            </Box>
                          )}
                          {tx.status === 'Success' && (
                            <Box sx={{ mt: 0.5 }}>
                              {actionLoading[tx.ID] ? (
                                <CircularProgress size={18} sx={{ color: '#c62828' }} />
                              ) : (
                                isAccountant ? "" : (
                                <Tooltip title="Mark as Failed">
                                  <Button size="small" variant="outlined" startIcon={<Delete />}
                                    onClick={() => { if (window.confirm('Mark this transaction as Failed?')) handleTxAction(tx.ID, 'Failed'); }}
                                    sx={{ borderRadius: 2, fontSize: 10, color: '#c62828', borderColor: '#c62828' }}>
                                    Reject
                                  </Button>
                                </Tooltip>)
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={collectDialog} onClose={() => setCollectDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Collect Fees — {selectedStudent?.full_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>

            {/* Payment Method */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1.5px solid #e8eaf6', borderRadius: 2, background: '#f9fafe' }}>
                <FormLabel sx={{ fontSize: 13, fontWeight: 700, color: '#1a237e', mb: 1, display: 'block' }}>
                  Payment Method *
                </FormLabel>
                <RadioGroup row value={collectForm.pay_method}
                  onChange={e => setCollectForm(p => ({ ...p, pay_method: e.target.value }))}>
                  <FormControlLabel value="Cash" control={
                    <Radio size="small" sx={{ color: '#1a237e', '&.Mui-checked': { color: '#1a237e' } }} />
                  } label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#2e7d32' }} />
                      <Typography fontSize={13} fontWeight={700}>Cash</Typography>
                    </Box>
                  } />
                  <FormControlLabel value="Online/Qr" control={
                    <Radio size="small" sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }} />
                  } label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#1565c0' }} />
                      <Typography fontSize={13} fontWeight={700}>Online/Qr</Typography>
                    </Box>
                  } />
                  <FormControlLabel value="DD/DBT" control={
                    <Radio size="small" sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }} />
                  } label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#1565c0' }} />
                      <Typography fontSize={13} fontWeight={700}>DD/DBT</Typography>
                    </Box>
                  } />
                  <FormControlLabel value="Edu Loan" control={
                    <Radio size="small" sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }} />
                  } label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#1565c0' }} />
                      <Typography fontSize={13} fontWeight={700}>Edu Loan</Typography>
                    </Box>
                  } />
                  <FormControlLabel value="NSP/SSP/FNP" control={
                    <Radio size="small" sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }} />
                  } label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#1565c0' }} />
                      <Typography fontSize={13} fontWeight={700}>NSP/SSP/FNP</Typography>
                    </Box>
                  } />
                </RadioGroup>
              </Box>
            </Grid>

            {/* Receipt No — label changes by method */}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small"
                label={collectForm.pay_method === 'Cash' ? 'Receipt Number *' : 'Transaction Number *' }
                placeholder={collectForm.pay_method === 'Cash' ? 'e.g. RCP-001' : 'e.g. TXN123456789' }
                value={collectForm.recipt_no} onChange={cf('recipt_no')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Receipt Name / Description" value={collectForm.recipt_name} onChange={cf('recipt_name')} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Tuition Fee *" type="number" value={collectForm.registrationFee} onChange={cf('registrationFee')}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Clinical Fee" type="number" value={collectForm.admissionFee} onChange={cf('admissionFee')}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Mess Fee" type="number" value={collectForm.transportFee} onChange={cf('transportFee')}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Hostel Fee" type="number" value={collectForm.miscellaneousFee} onChange={cf('miscellaneousFee')}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ p: 1.5, background: '#e8f5e9', borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography fontSize={14} fontWeight={700} color="#2e7d32">
                    Total: ₹{(
                      parseFloat(collectForm.registrationFee || 0) +
                      parseFloat(collectForm.admissionFee || 0) +
                      parseFloat(collectForm.transportFee || 0) +
                      parseFloat(collectForm.miscellaneousFee || 0)
                    ).toLocaleString()}
                  </Typography>
                  <Typography fontSize={11} color="text.secondary">Will be added as Pending — approve after verification</Typography>
                </Box>
                <Chip
                  label={collectForm.pay_method}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    background: collectForm.pay_method === 'Cash' ? '#e8f5e9' : '#e3f2fd',
                    color: collectForm.pay_method === 'Cash' ? '#2e7d32' : '#1565c0',
                    border: `1.5px solid ${collectForm.pay_method === 'Cash' ? '#2e7d32' : '#1565c0'}`,
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 2 }} disabled={receiptUploading}>
                {receiptUploading ? 'Uploading...' : receiptFile ? `✓ ${receiptFile.name}` : `Upload ${collectForm.pay_method === 'Online' ? 'Payment Screenshot' : 'Receipt Image'} (optional)`}
                <input type="file" hidden accept="image/*" onChange={e => {
                  const file = e.target.files[0];
                  if (file) { setReceiptFile(file); handleReceiptUpload(file); }
                }} />
              </Button>
              {receiptFile && !receiptUploading && (
                <Box component="img" src={URL.createObjectURL(receiptFile)} alt="receipt preview"
                  sx={{ mt: 1, width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 2 }} />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setCollectDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleCollectFees} variant="contained" disabled={collectSaving || receiptUploading}
            sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 2, fontWeight: 700, minWidth: 140 }}>
            {collectSaving ? <CircularProgress size={20} color="inherit" /> : 'Add as Pending'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!receiptDialog} onClose={() => setReceiptDialog(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Fee Receipt
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {receiptDialog && (
            <Box component="img" src={receiptDialog} alt="receipt"
              sx={{ width: '100%', borderRadius: 2, objectFit: 'contain' }} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button href={receiptDialog} target="_blank" variant="outlined" sx={{ borderRadius: 2 }}>
            Open Full Size
          </Button>
          <Button onClick={() => setReceiptDialog(null)} variant="contained"
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
